import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { useAppDetail } from "@/hooks/useApps";
import { useSelectedAppContext } from "@/lib/useSelectedAppContext";
import Layout from "@/components/layout/Layout";
import PageTitleSync from "@/components/layout/PageTitleSync";
import { lazyPage } from "@/components/layout/RouteFallback";
import PageHeader from "@/components/layout/PageHeader";
import { AuthProvider } from "@/lib/authContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { SelectedAppProvider } from "@/lib/selectedAppContext";

// User-facing IA-REDESIGN pages (BRIEF_ia-redesign-frontend-implementation § 8).
// Every page is code-split (ENG-APP-UX-TUNEUP Track F, floor row 10): `lazyPage`
// wraps `lazy()` in its own Suspense boundary inside the shell, so a route's chunk
// loads behind the header + sidebar with the delayed skeleton of the loading
// policy. The initial payload is the shell, the router and the English catalog.
const Home = lazyPage(() => import("@/pages/Home"));
const Recommendations = lazyPage(() => import("@/pages/Recommendations"));
const HypothesisDetail = lazyPage(() => import("@/pages/HypothesisDetail"));
const ExperimentsLayout = lazyPage(
  () => import("@/pages/experiments/ExperimentsLayout"),
);
const ExperimentsActive = lazyPage(() => import("@/pages/experiments/Active"));
const ExperimentsHistory = lazyPage(
  () => import("@/pages/experiments/History"),
);
const ExperimentsNew = lazyPage(() => import("@/pages/experiments/New"));
const ExperimentsDetail = lazyPage(() => import("@/pages/experiments/Detail"));
const Programs = lazyPage(() => import("@/pages/Programs"));
const ProgramsDetail = lazyPage(() => import("@/pages/programs/Detail"));
const ProgramsNew = lazyPage(() => import("@/pages/programs/New"));
const WorkflowsTemplates = lazyPage(() => import("@/pages/programs/Templates"));
const CrossAppExecutiveProgressReport = lazyPage(
  () => import("@/pages/reports/CrossAppExecutiveProgressReport"),
);
const Signals = lazyPage(() => import("@/pages/Signals"));
const EvidenceHypotheses = lazyPage(
  () => import("@/pages/evidence/Hypotheses"),
);
const EvidenceSignalSources = lazyPage(
  () => import("@/pages/evidence/SignalSources"),
);
const EvidenceReports = lazyPage(() => import("@/pages/evidence/Reports"));
const Creatives = lazyPage(() => import("@/pages/Creatives"));
const Team = lazyPage(() => import("@/pages/Team"));
const Settings = lazyPage(() => import("@/pages/Settings"));
const Onboarding = lazyPage(() => import("@/pages/Onboarding"));
const OnboardingPlan = lazyPage(() => import("@/pages/OnboardingPlan"));
const OnboardingConnectApp = lazyPage(
  () => import("@/pages/onboarding/ConnectApp"),
);
const OnboardingBlocked = lazyPage(() => import("@/pages/onboarding/Blocked"));
const PublicDashboard = lazyPage(() => import("@/pages/PublicDashboard"));

// Auth pages (ENG-AUTH-LOGIN-SIGNUP).
const Login = lazyPage(() => import("@/pages/Login"));
const Signup = lazyPage(() => import("@/pages/Signup"));
const ForgotPassword = lazyPage(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyPage(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazyPage(() => import("@/pages/VerifyEmail"));
const MagicLink = lazyPage(() => import("@/pages/MagicLink"));
const MagicLinkVerify = lazyPage(() => import("@/pages/MagicLinkVerify"));
const InvitationAccept = lazyPage(() => import("@/pages/InvitationAccept"));

const AdminAttribution = lazyPage(() => import("@/pages/AdminAttribution"));
const AdminCosts = lazyPage(() => import("@/pages/AdminCosts"));
const AdminLogin = lazyPage(() => import("@/pages/AdminLogin"));
const AdminSetPassword = lazyPage(() => import("@/pages/AdminSetPassword"));
import AdminProtectedRoute from "@/components/admin/AdminProtectedRoute";
import NotFound from "@/pages/NotFound";

// Engineering scaffolding (/_eng/* per IA-REDESIGN PO-1) is DEVELOPMENT-ONLY.
// These pages carry hardcoded demo/seed payloads (app_demo_1, ws_demo_1,
// exp_demo_1, SAMPLE_*) that must never ship to production. Both the lazy
// import() calls AND the route subtree below live inside this
// import.meta.env.DEV guard, so in a prod build `engPages` folds to null and
// Rollup statically drops the whole set — NO chunks are emitted. (A plain
// top-level `const X = lazy(() => import(...))` would still emit an orphan
// chunk even when its route usage is dead; gating the import() itself is what
// prevents emission.) Mirrors the import.meta.env.DEV demo-data gate already
// used in experiments/Active.tsx + experiments/New.tsx.
const engPages = import.meta.env.DEV
  ? {
      Layout: lazy(() => import("@/components/layout/EngineeringLayout")),
      AlertThresholds: lazy(() => import("@/pages/AlertThresholds")),
      CohortFlow: lazy(() => import("@/pages/CohortFlow")),
      Dashboard: lazy(() => import("@/pages/Dashboard")),
      Finalization: lazy(() => import("@/pages/Finalization")),
      GenerationPipeline: lazy(() => import("@/pages/GenerationPipeline")),
      OpsAndIntegrations: lazy(() => import("@/pages/OpsAndIntegrations")),
      QualifierCheck: lazy(() => import("@/pages/QualifierCheck")),
      Ranking: lazy(() => import("@/pages/Ranking")),
      ReasoningV2: lazy(() => import("@/pages/ReasoningV2")),
      RunLifecycle: lazy(() => import("@/pages/RunLifecycle")),
      SilentState: lazy(() => import("@/pages/SilentState")),
      SlopCheck: lazy(() => import("@/pages/SlopCheck")),
      ZeroSignal: lazy(() => import("@/pages/ZeroSignal")),
    }
  : null;

/**
 * Redirects a legacy route to its `/evidence/*` canonical while preserving the
 * query string + hash, so `/signals?focus=<id>` and
 * `/learnings/hypotheses?focus=<id>` provenance deep links keep working through
 * the ENG-EVIDENCE-CATEGORY-RENAME path change. A plain `<Navigate>` drops the
 * search string, which would break the `?focus=` provenance carriers.
 */
function RedirectWithQuery({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * The retired standalone Brief surface.
 *
 * It was folded into the Signals "App information" tab by
 * ENG-TRUE-DEDUP-UX-POLISH Track F2; that TAB is now retired too (operator
 * direction 2026-07-26: *"App information … isn't signals but also should be
 * removed. we have md context in ai settings and that should be only
 * working"*). The route therefore lands on the plain Data Signals list rather
 * than forcing a `?tab=` that no longer resolves — never a hard 404 for an
 * old bookmark. Query params + hash are preserved (e.g. the `?empty` QA
 * keyword).
 */
function RedirectToSignals() {
  const { search, hash } = useLocation();
  return <Navigate to={`/evidence/signals${search}${hash}`} replace />;
}

/**
 * The retired standalone Google-Ads observational view.
 *
 * It served BOTH iOS CPP (`param=ppid`) and Android CSL (`param=listing`)
 * routed-campaign rows; its content now lives on the per-page cards + the
 * per-channel modal of the platform-gated Content tabs (PO 2026-08-13, slice
 * C1+G). A static redirect to /creatives/cpp would send an ANDROID operator
 * on a double hop: Creatives hides the `cpp` tab off-platform and (since C10
 * M3-R16-1) redirects to the sibling `csl` tab with a one-line note about an
 * address the operator never typed. Resolve the destination from the selected
 * app's platform instead — never a hard 404, and never a wrong-platform tab.
 */
function RedirectToRoutedPagesTab() {
  const { search, hash } = useLocation();
  const { selectedAppId } = useSelectedAppContext();
  const { data: appDetail } = useAppDetail(selectedAppId);
  const target =
    appDetail?.platform === "android" ? "/creatives/csl" : "/creatives/cpp";
  return <Navigate to={`${target}${search}${hash}`} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <PageTitleSync />
        <AuthProvider>
          <Routes>
            {/* Public auth routes — rendered outside the app shell. */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/magic-link" element={<MagicLink />} />
            <Route path="/magic-link/verify" element={<MagicLinkVerify />} />
            {/* § 8b Add-person accept flow — the emailed single-use
              accept deep link lands here (ENG-WORKSPACE-INVITATION-
              DELIVERY scope 2). */}
            <Route path="/invitations/accept" element={<InvitationAccept />} />

            {/* Co-branded Executive Dashboard (PROD-PRICING-K) — public,
              unauthenticated, Enterprise-tier ONLY (server-gated via
              the dashboard token). Indistinguishable 404 + 410 templates
              per docs/product/experience-and-ui.md § Cross-cutting § Executive
              Dashboard "Tier-gate / token-absent renderings". */}
            <Route
              path="/dashboard/:workspaceToken"
              element={<PublicDashboard />}
            />

            {/* Admin realm (ENG-ADMIN-COST-DASHBOARD) — a SEPARATE auth realm,
              fully isolated from the customer app shell + session. Public admin
              login + emailed set-password provisioning (no signup), then an
              AdminProtectedRoute that checks the admin session (GET
              /api/v1/admin-auth/me) and renders /admin/costs inside the minimal
              AdminShell. A customer session NEVER reaches /admin/costs — the
              cross-workspace cost god-view is gated on the admin realm, not the
              customer ProtectedRoute. */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/set-password" element={<AdminSetPassword />} />
            <Route element={<AdminProtectedRoute />}>
              <Route path="/admin/costs" element={<AdminCosts />} />
            </Route>

            {/* Authenticated app shell. */}
            <Route
              element={
                <ProtectedRoute>
                  <SelectedAppProvider>
                    <Layout />
                  </SelectedAppProvider>
                </ProtectedRoute>
              }
            >
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="onboarding/plan" element={<OnboardingPlan />} />
              <Route
                path="onboarding/connect-app"
                element={<OnboardingConnectApp />}
              />
              <Route
                path="onboarding/blocked"
                element={<OnboardingBlocked />}
              />

              {/* User-facing IA */}
              <Route index element={<Home />} />
              <Route path="recommendations" element={<Recommendations />} />
              <Route
                path="recommendations/:hypothesisId"
                element={<HypothesisDetail />}
              />

              <Route path="experiments" element={<ExperimentsLayout />}>
                <Route index element={<Navigate to="active" replace />} />
                <Route path="active" element={<ExperimentsActive />} />
                {/* Pending review RETIRED as a page (ASOLOOP-UX item 9,
                  operator decision 2026-07-26): `pending_review` winners were
                  already Active rows (chipped + sorted first), so the page
                  duplicated Active. The workspace-wide AI-visual approval
                  queue it also hosted moved to Team. Route stays registered as
                  a redirect — never a hard 404. */}
                <Route
                  path="pending-review"
                  element={<Navigate to="/experiments/active" replace />}
                />
                <Route path="history" element={<ExperimentsHistory />} />
              </Route>
              {/* New is a standalone page (NOT a tab on the Active/History
                layout) per § 5c / Sub-9 — rendered outside ExperimentsLayout
                so it carries no Active/History tab bar. */}
              <Route path="experiments/new" element={<ExperimentsNew />} />
              {/* Decision queue (§ 5e) — folded into the Active tab (PO chat
                2026-08-01 "rewire page with 'pending decisions' on
                experiments into 'active'"; supersedes PO doc 2026-07-29
                item 1's standalone restore). The route stays registered as a
                deep-link alias so stale links land on the tab that now owns
                the queue; the static segment still outranks :state/:id. */}
              <Route
                path="experiments/decisions"
                element={<Navigate to="/experiments/active" replace />}
              />
              {/* Listing changes (RETIRED — ENG-LATAM-DUPLICATE-SURFACE-DEDUP
                Track C+D): the standalone /experiments/changes page is gone;
                its from/to audit + rollback badge/modal/toasts moved onto the
                History-route Detail page (§ 5d Rollback), immediately after
                Apply Winner. The route stays registered as a redirect — never
                a hard 404 — for old bookmarks/deep links. The BACKEND
                /api/v1/experiments/listing-changes endpoint is UNCHANGED
                (still the data source Detail's Rollback section reads via
                useExperimentChanges). */}
              <Route
                path="experiments/changes"
                element={<Navigate to="/experiments/history" replace />}
              />
              <Route
                path="experiments/:state/:id"
                element={<ExperimentsDetail />}
              />

              {/* Workflows (§ 11 rename of "Programs"; cycle programs spec at
                docs/product/programs.md). Route family stays /programs as the
                § 11b backward-compatible alias. /programs/{active,history}
                render the workflow list scoped; /programs/templates is the
                seeded-pattern catalog. Static segments outrank the :programId
                detail in React Router's ranking, so detail deep-links stay
                correct. */}
              <Route
                path="programs"
                element={<Navigate to="/programs/active" replace />}
              />
              <Route
                path="programs/active"
                element={<Programs scope="active" />}
              />
              {/* Workflow history folded into Experiments History with a
                  Workflow chip (ENG-EXPERIMENT-TYPE-TAXONOMY-REWORK); this
                  route had ZERO inbound links — kept only as a redirect for
                  old bookmarks (ENG-TRUE-DEDUP-UX-POLISH E8). */}
              <Route
                path="programs/history"
                element={<Navigate to="/experiments/history" replace />}
              />
              <Route
                path="programs/templates"
                element={<WorkflowsTemplates />}
              />
              <Route path="programs/new" element={<ProgramsNew />} />
              <Route path="programs/:programId" element={<ProgramsDetail />} />

              {/* V2 reports — workspace-wide multi-app rollup PDF
                (ENG-FE-V2-CROSS-APP-EXEC-REPORT). Pro+ tier-gated; the
                page renders the upgrade CTA on Starter tier. */}
              <Route
                path="reports/cross-app-executive-progress"
                element={<CrossAppExecutiveProgressReport />}
              />

              {/* Evidence (ENG-EVIDENCE-CATEGORY-RENAME) — sidebar category,
                NOT a route. The four sub-routes render live under /evidence/*;
                the parent /evidence path is intentionally NOT registered
                (clicking the Evidence sidebar parent navigates to
                /evidence/signals per § 11c defaultRoute). The page at
                /evidence/signals renders titled "Data signals". */}
              <Route path="evidence/signals" element={<Signals />} />
              <Route
                path="evidence/hypotheses"
                element={<EvidenceHypotheses />}
              />
              {/* Retired per § 11c (ENG-TRUE-DEDUP-UX-POLISH Track F2):
                brief content is the Signals "App information" filter tab;
                the old URL stays addressable as a query-preserving
                redirect for bookmarks + deep links. */}
              <Route path="evidence/brief" element={<RedirectToSignals />} />
              <Route
                path="evidence/signal-sources"
                element={<EvidenceSignalSources />}
              />
              {/* Analytics → Reports (§ 6f; operator direction chat
                2026-07-26). Five peer reports under one route, selected
                via `?tab=` — Content metrics / Channel metrics / Locales
                (the rollup trio, HTTP_API.md
                `GET /api/v1/apps/{app_id}/analytics/report-rollups`) plus
                the two per-object reports that used to be standalone
                Evidence pages. */}
              <Route path="evidence/reports" element={<EvidenceReports />} />
              {/* The two retired standalone routes. They land on their tab
                rather than 404ing: bookmarks survive, and the authored
                CPP/CSL approve+publish queue (§ 6d) inside the Targeting-
                pages tab is never orphaned. `replace` so browser-back does
                not bounce off the redirect. */}
              <Route
                path="evidence/targeting-pages"
                element={
                  <Navigate
                    to="/evidence/reports?tab=targeting-pages"
                    replace
                  />
                }
              />
              <Route
                path="evidence/keyword-metrics"
                element={
                  <Navigate
                    to="/evidence/reports?tab=keyword-metrics"
                    replace
                  />
                }
              />

              {/* The two standalone CPP/CSL performance pages are RETIRED
                (PO 2026-08-13, ENG-CONTENT-SURFACE-UX-PROGRAM slice R:
                "this should be deleted. instead, we must place total number
                of installs / conversions / visitors on CPP item sections on
                surface grid and per channel detailed stats upon opening
                CPP"). The INSTEAD half shipped in slice C1+G — Content →
                Custom product pages / Custom store listings carries the
                totals on each card and the per-channel breakdown in the
                card's expand modal, reading the SAME two endpoints
                (`useCppCrossSourceView` + `useGoogleAdsCppObservations` in
                CustomPageTab). Both routes stay REGISTERED as redirects —
                never a hard 404 — so old bookmarks/deep links land on the
                live surface (same convention as /pending-approvals and
                /evidence/targeting-pages above). `replace` so browser-back
                does not bounce off the redirect.

                Both land on /creatives/cpp: the cross-source rollup is
                per-CPP, and the Google-Ads observational read is CPP-primary
                too (`google-ads-cpp-observations` / `GoogleAdsCppObservationOut`,
                `param=ppid`) with the Android `param=listing` rows folded in
                as the CSL sibling — those render on /creatives/csl, which the
                Creatives tab bar only exposes for Android apps (an iOS app
                sent there is redirected to /creatives/cpp with a note,
                C10 M3-R16-1). */}
              <Route
                path="cpp-cross-source-view"
                element={<Navigate to="/creatives/cpp" replace />}
              />
              <Route
                path="google-ads-observational"
                element={<RedirectToRoutedPagesTab />}
              />

              {/* Legacy route redirects (ENG-EVIDENCE-CATEGORY-RENAME) — the
                pre-rename paths 301 to their /evidence/* canonical via
                <RedirectWithQuery>, which preserves the query string so
                /signals?focus=<id> + /learnings/hypotheses?focus=<id>
                provenance deep links keep working. /learnings/import (the
                former "Import" alias of Signal Sources) folds into
                /evidence/signal-sources. */}
              <Route
                path="signals"
                element={<RedirectWithQuery to="/evidence/signals" />}
              />
              <Route
                path="learnings/hypotheses"
                element={<RedirectWithQuery to="/evidence/hypotheses" />}
              />
              <Route path="learnings/brief" element={<RedirectToSignals />} />
              <Route
                path="learnings/signal-sources"
                element={<RedirectWithQuery to="/evidence/signal-sources" />}
              />
              <Route
                path="learnings/import"
                element={<RedirectWithQuery to="/evidence/signal-sources" />}
              />

              <Route
                path="creatives"
                element={<Navigate to="/creatives/screenshots" replace />}
              />
              <Route path="creatives/:tab" element={<Creatives />} />
              {/* App — per-app config category (§ 11b / § 11c). The four App
                config pages are canonical at /app/* (the sidebar App group
                destination); the /settings/* slugs below stay registered as
                deep-link aliases that render the same full-width app page
                (experience-and-ui.md § 11 Screen map — "/app/* aliases preserved for
                deep-linking parity"). Settings.tsx resolves both namespaces. */}
              <Route
                path="app"
                element={<Navigate to="/app/store-connections" replace />}
              />
              <Route path="app/:subpage" element={<Settings />} />
              <Route
                path="settings"
                element={<Navigate to="/settings/account" replace />}
              />
              <Route path="settings/:subpage" element={<Settings />} />

              {/* Team — workspace/org view (§ 11 top-level section; A7b, PO
                chat 2026-06-22): portfolio of all workspace apps + the
                relocated Organization-rules library (moved out of Settings →
                Executive Dashboard). */}
              <Route path="team" element={<Team />} />

              {/* The standing AI-visual approval queue is RETIRED OUTRIGHT
                (PO doc 2026-07-29 item 2): visuals are approved during
                experiment creation (wizard / pre-launch Manage visuals) or
                auto-shipped by autopilot — no approve collection surface.
                The route stays registered as a redirect — never a hard 404 —
                for old bookmarks/deep links. Creatives → Content rules keeps
                the per-app upload surface. */}
              <Route
                path="pending-approvals"
                element={<Navigate to="/experiments/active" replace />}
              />

              {/* Engineering scaffolding under /_eng/* — DEV-ONLY. The whole
                subtree (routes + lazy page chunks) is gated by `engPages`,
                which folds to null in production so Rollup drops it — and the
                pages' chunks — from the prod build entirely. experience-and-ui.md § 10
                BETA · ENG ONLY banner via EngineeringLayout; a single Suspense
                boundary on the layout element covers every lazy child route. */}
              {engPages && (
                <Route
                  path="_eng"
                  element={
                    <Suspense fallback={null}>
                      <engPages.Layout />
                    </Suspense>
                  }
                >
                  <Route path="dashboard" element={<engPages.Dashboard />} />
                  <Route
                    path="run-lifecycle"
                    element={<engPages.RunLifecycle />}
                  />
                  <Route
                    path="generation-pipeline"
                    element={<engPages.GenerationPipeline />}
                  />
                  <Route
                    path="finalization"
                    element={<engPages.Finalization />}
                  />
                  {/* Ranking is shared with the customer /recommendations
                      route (which supplies its own PageHeader) + embedded in
                      Home, so it cannot own an <h1> internally without
                      duplicating one there. The /_eng scaffolding mount gets
                      its heading here instead — matches the sibling /_eng
                      pages' PageHeader and satisfies axe page-has-heading-one
                      (ENG-A11Y-CONTRAST-AND-LABELS item 4). */}
                  <Route
                    path="ranking"
                    element={
                      <>
                        <PageHeader
                          title="Recommendations"
                          description="Engineering scaffolding for the Stage 7.5 split-card recommendations board."
                        />
                        <engPages.Ranking />
                      </>
                    }
                  />
                  <Route
                    path="qualifier"
                    element={<engPages.QualifierCheck />}
                  />
                  <Route path="slop" element={<engPages.SlopCheck />} />
                  <Route
                    path="silent-state"
                    element={<engPages.SilentState />}
                  />
                  <Route
                    path="reasoning-v2"
                    element={<engPages.ReasoningV2 />}
                  />
                  <Route path="cohort-flow" element={<engPages.CohortFlow />} />
                  <Route
                    path="ops-and-integrations"
                    element={<engPages.OpsAndIntegrations />}
                  />
                  <Route path="zero-signal" element={<engPages.ZeroSignal />} />
                  <Route path="alerts" element={<engPages.AlertThresholds />} />
                </Route>
              )}

              {/* Admin attribution — owner/billing-role admin dashboard, gated
                SERVER-SIDE on require_role; reached by direct URL (no
                customer-visible nav entry), 403 empty-state otherwise. NOTE:
                /admin/costs is NO LONGER a child here — the cross-workspace cost
                god-view moved to the SEPARATE admin-auth realm (top-level
                /admin/login → AdminProtectedRoute → /admin/costs,
                ENG-ADMIN-COST-DASHBOARD) so a customer session can never reach
                it. */}
              <Route path="admin">
                <Route path="attribution" element={<AdminAttribution />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
